import {
  Typography,
  Box,
  Tooltip,
  ClassNameMap,
  IconButton,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { isValid } from 'date-fns/esm';
import { Column } from 'react-data-grid';
import { logger } from 'logging-utils';
import CustomLink from '../../../../../elements/Aggregation/elements/Link';
import MultiLineEllipsis from '../../../../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { ExploitCode, ScaVulnerability } from '../../../types/issues-types';
import { TbNetwork } from 'react-icons/tb';
import { RiComputerLine } from 'react-icons/ri';
import { isDevelopment } from 'env-utils';

const CellWithTooltip = ({
  value,
  link,
  tooltip,
}: {
  value: string;
  link?: string;
  tooltip?: string;
}) => (
  <Tooltip
    arrow
    title={
      value ? (
        <Typography variant='body1' color='text.primary'>
          {tooltip || value}
        </Typography>
      ) : (
        ''
      )
    }>
    <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
      <MultiLineEllipsis variant='body2' lines={3}>
        {link ? <CustomLink href={link} value={value} /> : value}
      </MultiLineEllipsis>
    </Box>
  </Tooltip>
);

export const useTableConfig = ({
  text,
}: ClassNameMap<'text'>): Column<ScaVulnerability>[] => {
  return [
    {
      name: 'Vulnerability ID',
      key: 'cve',
      minWidth: 100,
      maxWidth: 150,
      formatter: ({ row }) => (
        <CellWithTooltip value={row.cve} link={row.cveLink} />
      ),
    },
    {
      name: 'CVSS',
      key: 'cvsVer',
      width: 80,
      minWidth: 80,
      formatter: ({ row }) => (
        <CellWithTooltip value={row.cvsVer || 'Not Available'} />
      ),
    },
    {
      name: 'CWE',
      key: 'cwe',
      width: 90,
      formatter: ({ row }) => {
        return row.cwe?.length ? (
          <Box
            py={1}
            alignItems='center'
            gap={0.5}
            display='flex'
            justifyContent='center'
            flexDirection='column'
            height='100%'>
            {row.cwe.map(cwe => (
              <Tooltip arrow title={cwe.name} key={cwe.name}>
                <div style={{ height: 'min-content' }}>
                  <CustomLink
                    lineHeight={1}
                    value={cwe.shortName}
                    href={cwe.url}
                  />
                </div>
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Typography variant='body2'>Not Available</Typography>
        );
      },
    },
    ...(isDevelopment()
      ? [
          {
            name: 'Library',
            key: 'libName',
            width: 80,
            minWidth: 80,
            formatter: ({ row }) => <CellWithTooltip value={row.libName} />,
          },
          {
            name: 'Ver',
            key: 'libVer',
            width: 80,
            minWidth: 80,
            formatter: ({ row }) => <CellWithTooltip value={row.libVersion} />,
          },
          {
            name: 'Level',
            key: 'chainDepth',
            width: 50,
            minWidth: 50,
            formatter: ({ row }) => (
              <CellWithTooltip
                value={row.chainDepth}
                tooltip={row.dependencyChain}
              />
            ),
          },
        ]
      : []),

    {
      name: 'Description',
      minWidth: 100,
      key: 'description',
      formatter: ({ row }) => <CellWithTooltip value={row.description} />,
    },

    {
      name: 'Exploit in the Wild',
      key: 'exploitInTheWildLink',
      headerCellClass: text,
      width: 80,
      formatter: ({ row }) => (
        <CustomLink
          value={row.exploitInTheWildLink ? 'Yes' : 'No'}
          href={row.exploitInTheWildLink}
        />
      ),
    },
    {
      name: 'Attack Vector',
      headerCellClass: text,
      width: 60,
      minWidth: 60,
      key: 'exploitRequirement',
      formatter: ({ row }) => (
        <Tooltip arrow title={row.exploitRequirement || ''}>
          <IconButton size='small'>
            {row.exploitCode === ExploitCode.Local && <RiComputerLine />}
            {row.exploitCode === ExploitCode.Network && <TbNetwork />}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      name: 'Discovered',
      key: 'dateDiscovered',
      minWidth: 110,
      width: 110,
      formatter: ({ row }) => {
        let date;
        try {
          date = row.dateDiscovered && new Date(row.dateDiscovered);
        } catch {
          logger.warn('Failed to parse the date', row);
        }
        return (
          <Tooltip arrow title={row.dateDiscovered || ''}>
            <Typography variant='body2' color='text.primary'>
              {date && isValid(date)
                ? formatDistanceToNow(date, { addSuffix: true })
                : 'Not Available'}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      name: 'Minor Version with Fix',
      minWidth: 100,
      maxWidth: 100,
      width: 100,
      key: 'minorVerWithFix',
      headerCellClass: text,
      formatter: ({ row }) => <CellWithTooltip value={row.minorVerWithFix} />,
    },
    {
      name: 'Major Version with Fix',
      minWidth: 100,
      maxWidth: 100,
      width: 100,
      headerCellClass: text,
      key: 'majorVerWithFix',
      formatter: ({ row }) => <CellWithTooltip value={row.majorVerWithFix} />,
    },
    {
      name: 'Severity',
      key: 'originalSeverity',
      minWidth: 80,
      maxWidth: 80,
      width: 80,
      formatter: ({ row }) => <CellWithTooltip value={row.originalSeverity} />,
    },
  ];
};
